/**
 * Date formatting utilities for converting between ISO 8601 and YYYY-MM-DD formats
 */

/**
 * Converts ISO 8601 date string to YYYY-MM-DD format for HTML date inputs
 * @param isoDate - ISO 8601 date string (e.g., "2024-01-15T10:00:00.000Z") or null
 * @returns YYYY-MM-DD string (e.g., "2024-01-15") or null
 */
export function formatDateForForm(isoDate: string | null): string | null {
  if (!isoDate) return null;
  return isoDate.split("T")[0];
}

/**
 * Converts YYYY-MM-DD format to ISO 8601 date string for API submission
 * @param dateString - YYYY-MM-DD string (e.g., "2024-01-15"), ISO string, null, or undefined
 * @returns ISO 8601 string (e.g., "2024-01-15T00:00:00.000Z"), null, or undefined
 */
export function formatDateForApi(dateString: string | null | undefined): string | null | undefined {
  if (!dateString) return dateString;
  if (dateString.includes("T")) return dateString; // Already ISO format
  return `${dateString}T00:00:00.000Z`;
}

function parseDisplayDate(dateString: string): Date {
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateString);

  if (dateOnly) {
    return new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]));
  }

  return new Date(dateString);
}

/**
 * Formats a date string in long format (e.g., "15 January 2024")
 * @param dateString - ISO 8601 date string or date string
 * @returns Formatted date string
 */
export function formatDateLong(dateString: string): string {
  const date = parseDisplayDate(dateString);
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

/**
 * Formats a date string in short format (e.g., "15/01/2024")
 * @param dateString - ISO 8601 date string or date string
 * @returns Formatted date string
 */
export function formatDateShort(dateString: string): string {
  const date = parseDisplayDate(dateString);
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

/**
 * Validates a date string is either empty (optional) or a valid YYYY-MM-DD date
 */
export function isValidDateString(value: string): boolean {
  if (!value) return true;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(value);
  return !Number.isNaN(date.getTime());
}

export function formatDateTimeShort(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}
